import React, { useState, useEffect } from 'react';
import { useLocation, useHistory, Redirect } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import LoginForm from './containers/LoginForm';
import { FullPage } from '../../layouts';
import { Container, Logo, SlideOpen, Text, NotificationModal } from '../../components';
import { routes } from '../../routes/Routes';
import { getSearchParam } from '../../utils/url';
import { authenticationService } from '../../services';
import { actions as sessionActions } from '../../redux/session';
import { identifyUser, trackEvent, EVENT_TYPES } from '../../api/analytics';

const message = {
  error: 'Your password reset link has expired. Please ask for a new password reset email.',
  success: 'Your new password has been sent to your e-mail.'
};

// type can be error or success
const resetPasswordStatusInitialValue = Object.freeze({
  type: 'error',
  message: ''
});

const Login = () => {
  const [showFields, setShowFields] = useState(false);
  const [resetPasswordStatus, setResetPasswordStatus] = useState(resetPasswordStatusInitialValue);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  // this hook controls the form slider
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowFields(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  // this hook manages the login route with passwordReset searchParam
  useEffect(() => {
    const isResetPassword = getSearchParam(location.search, 'passwordReset');

    if (isResetPassword === 'true') {
      setResetPasswordStatus({ type: 'success', message: message.success });
    } else if (isResetPassword === 'false') {
      setResetPasswordStatus({ type: 'error', message: message.error });
    }

    return () => {
      setResetPasswordStatus(resetPasswordStatusInitialValue);
    };
  }, [location]);

  const handleLoginSuccess = (response) => {
    authenticationService.storeUserData(response);

    dispatch(sessionActions.logIn({
      customerCode: response.customerCode,
      isGlobalConfEditCustomer: response.isGlobalConfEditCustomer
    }));

    // Track events in Analytics
    identifyUser(response);
    trackEvent(EVENT_TYPES.USER_LOGGED_IN);

    // Check for returnUrl and redirect if present
    const returnUrl = getSearchParam(location.search, 'returnUrl');
    if (returnUrl) {
      window.location.href = returnUrl;
      return;
    }

    let to = routes.home;
    const representatives = response.representatives.filter((rep) => !rep.isHome);

    if (response.representatives && representatives.length > 0) {
      to = routes.representative;
    } else if (response.askAdditionalInformation) {
      to = routes.askAdditionalInformation;
    } else if (location.state?.from) {
      to = location.state.from;
    }

    history.push(to);
  };

  const currentUser = authenticationService.currentUserValue;

  if (currentUser && currentUser.token) {
    return <Redirect to={routes.home} />;
  }

  return (
    <>
      <FullPage>
        <Container fullscreen verticalAlign="center" horizontalAlign="center">
          <Logo />

          <SlideOpen open={showFields}>
            <LoginForm onLoginSuccess={handleLoginSuccess} />
          </SlideOpen>
        </Container>

        <Container fullscreen bottom>
          <Text>
            All rights reserved &nbsp;
            <time>{new Date().getFullYear()}</time>
          </Text>
        </Container>
      </FullPage>

      <NotificationModal
        message={resetPasswordStatus.message}
        type={resetPasswordStatus.type}
        showModal={!!resetPasswordStatus.message && !!resetPasswordStatus.type}
        onCancel={() => setResetPasswordStatus(resetPasswordStatusInitialValue)}
        position="right"
      />
    </>
  );
};

export default Login;
